/**
 * Wizard-specific schema additions on top of the canonical Project form
 * schema (which lives in `@/entities/project/model/form-schema.ts` and is
 * re-exported from `@/entities/project`).
 *
 * Hoisted:
 *   - buildCreateProjectSchema, defaultFormData, CreateProjectFormData
 *     (the form shape itself) → `entities/project` (canonical)
 *   - MARKET_OPTIONS, PROJECT_TYPE_OPTIONS, STATUS_OPTIONS, REQUIRED_FIELDS,
 *     isFieldRequired (UI-option arrays + helpers derived from the entity
 *     constant maps) → `entities/project` (canonical)
 * What stays here:
 *   - WIZARD_STEPS / WIZARD_STEP_ARRAY — wizard-specific step metadata
 *     (the wizard owns the "intake → details" 2-step flow; no other slice
 *     needs this).
 *
 * Consumers can import either from this slice barrel (kept for transitional
 * convenience and the canonical re-exports are still exposed) or directly
 * from `@/entities/project` for the form-schema surface (preferred for
 * new code outside the wizard).
 */
import type { CreateProjectFormData } from '@/entities/project';

// Re-export the canonical entity-form-schema bits so existing
// `from '@/features/create-project/model/schema'` consumers keep working
// without churn. New code should prefer `from '@/entities/project'`.
export {
  buildCreateProjectSchema,
  defaultFormData,
  DESCRIPTION_MAX_LENGTH,
  getMarketLabel,
  isFieldRequired,
  MARKET_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  STATUS_OPTIONS,
  TITLE_MAX_LENGTH,
} from '@/entities/project';
export type { CreateProjectFormData } from '@/entities/project';

interface WizardStep {
  id: string;
  requiredFields: readonly (keyof CreateProjectFormData)[];
}

export const WIZARD_STEPS = {
  intake: {
    id: 'intake',
    requiredFields: [],
  },
  details: {
    id: 'details',
    requiredFields: ['type', 'title', 'description', 'markets'],
  },
} as const satisfies Record<string, WizardStep>;

export const WIZARD_STEP_ARRAY = Object.values(WIZARD_STEPS);
